import {Component} from "@angular/core";
import {
    TCI_TOOLS_ENV_TYPE,
    ITCITenantAppCategory,
    ITCITenantComponentOptions,
    TCITenantAppType,
    TCITenantEnvTools,
    TCITenantAppCategory,
    TCITenantSelectorType,
    TCI_APP_FEATURE_TYPE,
    ITCIAppFeatures,
} from "@tci/common";
import {TCITenantRemoteServices} from "../ng-tenant/tci-tenant-remote-services";
import {ICategoryFilterOptions, getCategoryFilterOptions} from "../ng-shared/ng-util/tools";
import {NgEnvToolsComponentInput} from "./tool-loader/ng-env-tools-loader.component";
import {TCITenantMenuServices} from "../ng-tenant/tci-tenant.menu";
import {NgUserService} from "../ng-shared/ng-services/ng-user.service";
import {NgAppFeatureToggleService} from "../ng-shared/ng-services/ng-app-feature-toggle.service";

import {Observable} from "rxjs";
import * as lodash from "lodash";

interface IToolCategory {
    key: string;
    value: string;
}

interface IToolCategoryMap {
    category: IToolCategory;
    tools: ITCITenantComponentOptions<TCI_TOOLS_ENV_TYPE>[];
}

interface IStoredFilter {
    category: string;
}

interface ITCICompOptions extends ITCITenantComponentOptions<string | number> {
}

const FILTER_LABEL_ALL = "All";
const FILTER_CATEGORIES = [FILTER_LABEL_ALL, ...lodash.map(TCITenantAppCategory.getAllCategories(), d => d.label)];
const ENV_TOOLS_SESSION_ITEM = "TCI-ENV-TOOLS-SESSION-DATA";

@Component({
    selector: "ng-env-tools",
    templateUrl: "./ng-env-tools.component.html",
    styleUrls: ["./ng-env-tools.component.less"]
})
export class NgEnvToolsComponent {
    private allToolCategories: IToolCategoryMap[] = [];
    FILTER_CATEGORIES: ICategoryFilterOptions[] = [];
    filteredToolCategories: IToolCategoryMap[] = [];
    categoryFilter: string = FILTER_LABEL_ALL;
    loadingText = "Loading...";
    isReady = false;

    private remoteSrv: string[] = [];

    constructor(
        tenantRemoteServices: TCITenantRemoteServices,
        private tenantMenuSrv: TCITenantMenuServices,
        private userService: NgUserService,
        private featureToggleService: NgAppFeatureToggleService) {
        tenantRemoteServices.getRemoteServices()
            .map(remoteSrv => remoteSrv.map(srv => TCITenantAppType.fromString(srv.name).toString()))
            .subscribe(remoteSrv => {
                this.remoteSrv = remoteSrv;
                const obs: Observable<ITCITenantComponentOptions<string | number>[]>[] = [];
                TCITenantEnvTools.getAllItems() // Get all env menu items
                    .filter(item => item.getTenantTypes().filter(type => remoteSrv.includes(type.toString())).length > 0)
                    .forEach(componentOption =>
                        componentOption.getTenantTypes()
                            .forEach(tenant => {
                                /**
                                 * This is only an exception for a selector from a single tenant in the tools&env
                                 * Usually Tools&Env selectors have more than one tenant type where different
                                 * tenant externalUrlSuffix cannot be used for one single component option.
                                 * if the tenant has not declared in preloadmap then the tools&env externalUrl
                                 * value will be used
                                 */
                                const externalUrlSuffix = this.getSuffix(componentOption.getComponentSelectorType(), tenant);
                                if (componentOption.getTenantTypes().length === 1 && externalUrlSuffix !== null) {
                                    componentOption.setExternalUrl(externalUrlSuffix);
                                }
                                const input = new NgEnvToolsComponentInput(
                                    TCITenantAppType.createPseudoTenant(...TCITenantAppType.getAllTypes()),
                                    this.userService,
                                    this.featureToggleService,
                                    componentOption);
                                input.tenantAppType = tenant;
                                obs.push(this.tenantMenuSrv.getEnvToolsMenuItems(componentOption, input));
                            })
                    );

                Observable.merge(...obs)
                    .filter(res => res.length > 0)
                    .toArray()
                    .map(res => lodash.uniqBy([].concat.apply([], res), (opt: any) => opt.getRouteId()))
                    .subscribe(res => {
                      this.featureToggleService.getAppFeatures()
                          .subscribe(resFeature => {
                              this.buildItems(res, resFeature);
                          });
                    });
            });
    }

    public getSuffix(selector: TCITenantSelectorType, tenantType: TCITenantAppType): string {
        const preloadMap = this.tenantMenuSrv.getTenantPreloadMap(tenantType);
        if (preloadMap) {
            const preloadMapData = preloadMap[selector.toString()];
            if (preloadMapData && preloadMapData.externUrlSuffix) {
                return preloadMapData.externUrlSuffix;
            }
        }
        return null;
    }

    private buildItems(items: ITCICompOptions[], appFeatures: ITCIAppFeatures) {
        // Agroup the categories and available-tenant tools
        this.allToolCategories = lodash.map(TCI_TOOLS_ENV_TYPE, (value, key) => {
            const category: IToolCategory = {key, value};
            return {
                category,
                tools: items.filter(item => item.getOptionType() === category.value)
            };
        }).filter(cat => cat.tools.length > 0);

        // Note: Hide feature toggle items, so far no menu item is hidden, but this is the place to do it
        // this.hideFeatureToggleItem(appFeatures);

        // Count avaliable-tenant tools for filters
        this.FILTER_CATEGORIES = getCategoryFilterOptions(FILTER_LABEL_ALL)
            .map(filterOpt => {
                if (filterOpt.category.id === FILTER_LABEL_ALL) {
                    filterOpt.counter = this.allToolCategories.map(toolCat => toolCat.tools.length).reduce((n1, n2) => n1 + n2, 0);
                } else {
                    this.allToolCategories.forEach(toolCat => {
                        toolCat.tools.forEach(tool => {
                            const toolTenants = tool.getTenantTypes();
                            for (const i in toolTenants) {
                                if (toolTenants[i] && this.remoteSrv.includes(toolTenants[i].toString())) {
                                    const toolCategory = TCITenantAppCategory.fromTenantType(toolTenants[i]);
                                    if (toolCategory && toolCategory.id === filterOpt.category.id) {
                                        filterOpt.counter++;
                                        break;
                                    }
                                }
                            }
                        });
                        // sort item by position number
                        toolCat.tools = lodash.sortBy(toolCat.tools, "position");
                    });
                }
                return filterOpt;
            })
            .filter(opt => opt.counter > 0);
        this.restoreFilterStage();
        this.filterToolCategories();
        this.isReady = true;
    }

    /**
     * hide menu item in env & tools page if the related feature toggle is not enabled
     * @param appFeatures
     */
    hideFeatureToggleItem(appFeatures: ITCIAppFeatures) {
        this.allToolCategories.forEach(cat => {
            cat.tools.forEach((tool, index) => {
                switch (tool.getRouteId()) {
                    // Note: if alert item is not enabled in feature toggle for env & tools page, the alert item should be hidden.
                    // case TCITenantEnvTools.TCI_ENV_TOOLS_ALERT.getRouteId():
                    //     if (!appFeatures[TCI_APP_FEATURE_TYPE.ENV_ALERT].enabled) {
                    //         cat.tools.splice(index, 1);
                    //     }
                    //     break;
                }
            });
        });
    }

    filterByCategory(opt: ITCITenantAppCategory) {
        const filteredCategory: string = opt.id === FILTER_LABEL_ALL ? FILTER_LABEL_ALL : opt.id;
        if (this.categoryFilter !== filteredCategory) {
            this.categoryFilter = filteredCategory;
            this.filterToolCategories();
        }
    }

    private filterToolCategories(): void {
        if (this.categoryFilter === FILTER_LABEL_ALL) {
            this.filteredToolCategories = this.allToolCategories;
        } else {
            this.filteredToolCategories = lodash.cloneDeep(this.allToolCategories)
                .map((cat: IToolCategoryMap) => {
                    cat.tools = cat.tools.filter(
                        tool => tool.getTenantTypes().some(type => {
                            const category = TCITenantAppCategory.fromTenantType(type);
                            return category && category.id === this.categoryFilter;
                        })
                    );
                    return cat;
                })
                .filter(cat => cat.tools.length > 0);
        }

        this.storeFilterStage();
    }

    getTenantType(option: ITCITenantComponentOptions<TCI_TOOLS_ENV_TYPE>) {
        return lodash.first(option.getTenantTypes());
    }

    storeFilterStage() {
        const filters: IStoredFilter = {category: this.categoryFilter};
        sessionStorage.setItem(ENV_TOOLS_SESSION_ITEM, JSON.stringify(filters));
    }

    restoreFilterStage() {
        const filterStageData = sessionStorage.getItem(ENV_TOOLS_SESSION_ITEM);
        if (!lodash.isEmpty(filterStageData)) {
            try {
                const filterStage: IStoredFilter = JSON.parse(filterStageData);
                if (TCITenantAppCategory.fromId(filterStage.category)) {
                    this.categoryFilter = filterStage.category;
                } else {
                    this.categoryFilter = FILTER_LABEL_ALL;
                    this.storeFilterStage();
                }
            } catch (e) {
                console.error("Env&Tools stage restore incomplete", e);
                this.categoryFilter = FILTER_LABEL_ALL;
            }
        } else {
            this.categoryFilter = FILTER_LABEL_ALL;
        }
    }

    // IPAS-16374 - Identify tenant availability for icon displaying
    tenantAvailabilityIconCheck(tenantStrName: string): boolean {
        return this.remoteSrv.includes(tenantStrName);
    }
}
